import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs/internal/Subscription';

import { AuthService } from '../../services/auth.service';
import { PatientAccessService } from '../../services/patient-access.service';
import { RouteValidationService } from '../../services/route-validation.service';
import { SafeLoggerService } from '../../services/safe-logger.service';
import { SessionStorageService } from '../../services/session-storage.service';

@Component({
    template: `<app-signin *ngIf="!userSignedIn"></app-signin>`,
    styles: [],
    standalone: false
})
export class LoginPageComponent implements OnInit {
  subscription: Subscription;
  userSignedIn = false;
  constructor(
    private router: Router,
    private auth_$: AuthService,
    private route: ActivatedRoute,
    private sessionStorage_$: SessionStorageService,
    private routeValidation_$: RouteValidationService,
    private patientAccess_$: PatientAccessService,
    private safeLogger: SafeLoggerService
  ) {}

  ngOnInit() {
    this.auth_$.auth.onAuthStateChanged(user => {
      if (user) {
        this.userSignedIn = true;
        // Check for referer in query params or session storage
        let referer = this.route.snapshot.queryParams['referer'] || this.sessionStorage_$.getReferer();
        
        // Validate and sanitize referer to prevent open redirect vulnerabilities
        referer = this.routeValidation_$.sanitizeReferer(referer);
        
        if (!referer) {
          this.safeLogger.warn('LoginPageComponent: Invalid or missing referer, will redirect to home');
        }
        
        this.safeLogger.log('LoginPageComponent: User signed in, proceeding with redirect check');
        
        // Wait for userIsSignedIn to be true and userData to be loaded before redirecting
        const checkAndRedirect = async () => {
          const isSignedIn = this.auth_$.userIsSignedIn.getValue();
          const userData = this.auth_$.userData.getValue();
          
          this.safeLogger.log('LoginPageComponent: Checking auth state - isSignedIn:', isSignedIn, 'userData keys:', Object.keys(userData).length);
          
          if (isSignedIn && Object.keys(userData).length > 0) {
            // User is fully authenticated
            
            // If referer is a patient overview route, validate patient access
            if (referer && this.routeValidation_$.isPatientOverviewRoute(referer)) {
              const patientId = this.routeValidation_$.extractPatientIdFromRoute(referer);
              
              if (patientId) {
                this.safeLogger.log('LoginPageComponent: Validating patient access for referer route');
                const accessResult = await this.patientAccess_$.validatePatientAccess(patientId);
                
                if (!accessResult.hasAccess) {
                  this.safeLogger.warn('LoginPageComponent: Patient access denied');
                  // Show user-friendly error message
                  this.auth_$.snackBar.open(
                    accessResult.error === 'Patient not found'
                      ? 'Patient not found'
                      : 'You do not have access to this patient',
                    'Close',
                    { duration: 5000 }
                  );
                  // Redirect to home instead
                  this.sessionStorage_$.removeReferer();
                  setTimeout(() => {
                    this.router.navigate(['/']);
                  }, 500);
                  return;
                }
                
                this.safeLogger.log('LoginPageComponent: Patient access validated, proceeding with redirect');
              }
            }
            
            // Proceed with redirect
            if (referer && referer !== 'null' && referer !== '/' && referer !== 'null') {
              this.sessionStorage_$.removeReferer();
              // Use a delay to ensure AuthGuard sees the updated state
              setTimeout(() => {
                this.router.navigateByUrl(referer).catch((error) => {
                  this.safeLogger.error('LoginPageComponent: Failed to navigate to referer', error);
                  this.safeLogger.warn('LoginPageComponent: Redirecting to / instead');
                  this.router.navigate(['/']);
                });
              }, 500);
            } else {
              this.safeLogger.log('LoginPageComponent: No valid referer, redirecting to /');
              this.router.navigate(['/']);
            }
          } else {
            // Not ready yet, wait and check again (max 5 seconds)
            this.safeLogger.log('LoginPageComponent: Auth state not ready yet, waiting...');
            const startTime = Date.now();
            const maxWaitTime = 5000;
            
            const waitForAuth = () => {
              const currentIsSignedIn = this.auth_$.userIsSignedIn.getValue();
              const currentUserData = this.auth_$.userData.getValue();
              
              if (currentIsSignedIn && Object.keys(currentUserData).length > 0) {
                checkAndRedirect().catch(err => this.safeLogger.warn('LoginPageComponent: checkAndRedirect failed', err));
              } else if (Date.now() - startTime < maxWaitTime) {
                setTimeout(waitForAuth, 200);
              } else {
                this.safeLogger.warn('LoginPageComponent: Timeout waiting for auth state, redirecting to /');
                this.router.navigate(['/']);
              }
            };
            
            setTimeout(waitForAuth, 200);
          }
        };
        
        // Wait a bit for auth state to propagate, then start checking
        setTimeout(() => checkAndRedirect().catch(err => this.safeLogger.warn('LoginPageComponent: checkAndRedirect failed', err)), 300);
      }
    });

  }
}
